import {
  Building2,
  Users,
  GitBranch,
  Layers,
  Compass,
  Tag,
  Database,
  History,
  Boxes,
  Wrench,
  Cpu,
  BarChart3,
  TrendingUp,
  Recycle,
  Star,
} from "lucide-react";

import type { SettingsEntry } from "./settingsSearchIndex";

/**
 * Searchable entries for the Platform Settings sidebar.
 * All entries are platform-admin-only, so no extra gating is applied —
 * the Platform Settings area itself is already access-controlled.
 */
export const platformEntries: SettingsEntry[] = [
  {
    id: "pCompanies",
    titleKey: "nav.companies",
    path: "/platform/companies",
    icon: Building2,
    keywords: ["tenants", "organizations", "accounts", "create company"],
    topLevel: true,
    group: "company",
  },
  {
    id: "pCompanyUsers",
    titleKey: "nav.companyUsers",
    parentId: "pCompanies",
    path: "/platform/companies",
    icon: Users,
    keywords: ["members", "accounts", "assign company", "people"],
    group: "company",
  },
  {
    id: "pWorkflows",
    titleKey: "nav.workflows",
    path: "/platform/workflows",
    icon: GitBranch,
    keywords: ["procedures", "steps", "phases", "repair", "refurbishment", "harvesting"],
    topLevel: true,
    group: "company",
  },
  {
    id: "pWorkflowDefinitions",
    titleKey: "nav.workflowDefinitions",
    parentId: "pWorkflows",
    path: "/platform/companies",
    icon: Layers,
    keywords: ["definitions", "templates", "designer", "flow"],
    group: "company",
  },
  {
    id: "pNavigation",
    titleKey: "nav.navigation",
    path: "/platform/companies",
    icon: Compass,
    keywords: ["menu", "tabs", "links", "sidebar"],
    topLevel: true,
    group: "company",
  },
  {
    id: "pBrands",
    titleKey: "nav.brands",
    path: "/platform/brands",
    icon: Tag,
    keywords: ["manufacturers", "brand list", "aliases"],
    topLevel: true,
    group: "company",
  },
  {
    id: "pBrandDecoding",
    titleKey: "nav.brandDecoding",
    parentId: "pBrands",
    path: "/platform/brands/decoding",
    icon: Tag,
    keywords: ["decoding", "serial", "production date", "type shield", "ocr", "aliases"],
    group: "company",
  },
  {
    id: "pDecoderCoverage",
    titleKey: "nav.decoderCoverage",
    parentId: "pBrands",
    path: "/platform/brands/decoding-coverage",
    icon: BarChart3,
    keywords: ["coverage", "decoder", "statistics", "date decoding"],
    group: "company",
  },
  {
    id: "pData",
    titleKey: "nav.data",
    path: "/platform/data",
    icon: Database,
    keywords: ["catalogue", "configuration", "engine", "defaults"],
    topLevel: true,
    group: "company",
  },
  {
    id: "pApplianceCategories",
    titleKey: "nav.applianceCategories",
    parentId: "pData",
    path: "/platform/data/appliance-categories",
    icon: Boxes,
    keywords: ["categories", "appliance", "types", "eprel"],
    group: "company",
  },
  {
    id: "pRecommendationEngine",
    titleKey: "nav.recommendationEngine",
    parentId: "pData",
    path: "/platform/data/recommendation-engine",
    icon: Cpu,
    keywords: ["harvest", "rules", "engine", "recommendation", "upload", "xlsx"],
    group: "company",
  },
  {
    id: "pSpareParts",
    titleKey: "nav.spareParts",
    parentId: "pData",
    path: "/platform/data/spare-parts",
    icon: Wrench,
    keywords: ["parts", "components", "part types", "catalogue"],
    group: "company",
  },
  {
    id: "pBrandHarvestDefaults",
    titleKey: "entries.brandHarvestDefaults",
    parentId: "pData",
    path: "/platform/data/brand-harvest-defaults",
    icon: Recycle,
    keywords: ["brand", "harvest", "components", "defaults", "mapping"],
    group: "company",
  },
  {
    id: "pGradePricing",
    titleKey: "entries.gradePricingDefaults",
    parentId: "pData",
    path: "/platform/data/grade-pricing",
    icon: Star,
    keywords: ["grade", "pricing", "a b c", "resale", "percentage", "defaults"],
    group: "company",
  },
  {
    id: "pAppliancePrices",
    titleKey: "entries.appliancePrices",
    parentId: "pData",
    path: "/platform/data/appliance-prices",
    icon: TrendingUp,
    keywords: ["price", "tracker", "tweakers", "geizhals", "new appliance", "market"],
    group: "company",
  },
  {
    id: "pRefurbPrices",
    titleKey: "entries.refurbPrices",
    parentId: "pData",
    path: "/platform/data/refurbished-prices",
    icon: TrendingUp,
    keywords: ["refurbished", "price", "tracker", "willhaben", "resale", "market", "listings"],
    group: "company",
  },
  {
    id: "pUsers",
    titleKey: "nav.platformUsers",
    path: "/platform/users",
    icon: Users,
    keywords: [
      "users",
      "accounts",
      "superadmin",
      "console",
      "identity",
      "delete user",
      "password reset",
      "platform operators",
    ],
    topLevel: true,
    group: "company",
  },

  {
    id: "pAuditLog",
    titleKey: "nav.auditLog",
    path: "/platform/audit-log",
    icon: History,
    keywords: ["audit", "log", "history", "changes", "events"],
    topLevel: true,
    group: "company",
  },
];

export const topLevelPlatform = platformEntries.filter((e) => e.topLevel);

export function findPlatformEntry(id: string) {
  return platformEntries.find((e) => e.id === id);
}
